<template>
  <div>
    <app-spinner v-if="!is_loaded"/>
    <template v-else>
      <app-cells><h1 class="title">Contract renewals</h1></app-cells>
      <app-cells>
        <h2 class="title">Contract expire in next 30 days</h2>
      </app-cells>
      <v-client-table
        :data="table_contract_expire.items"
        :columns="table_contract_expire.columns"
        :options="table_contract_expire.options"
        class="table-default"
        :class="{ 'table-default--loading': contract_expire_preloader }"
      >
        <template #h__employee><div class="table-default__left">Name</div></template>
        <template #h__department><div class="table-default__left">Department</div></template>
        <template #h__position><div class="table-default__left">Position</div></template>
        <template #h__contract_expire><div class="table-default__left">Contract expire</div></template>
        <template #h__seniority><div class="table-default__left">Seniority</div></template>
        <template #employee="{ row }">
          <div class="table-default__left table-default__left--photo">
            <div class="table-default__photo">
              <img :src="getUserAvatar(row.employee.crop.crop_url, row.employee.gender?.code || null)" alt="Avatar">
            </div>
            <router-link :to="{ name: 'employees-contracts-salaries', params: { id: row.employee.id } }" class="table-default__link-border">
              {{ row.employee.full_name }}
            </router-link>
          </div>
        </template>
        <template #department="{ row }"><div class="table-default__left">{{ row.employee.department.name }}</div></template>
        <template #position="{ row }"><div class="table-default__left">{{ row.employee.position.name }}</div></template>
        <template #contract_expire="{ row }">
          <div class="table-default__left">{{ dateFormat(row.to_date) }}</div>
        </template>
        <template #seniority="{ row }">
          <div class="table-default__left">
            {{ row.employee?.seniority }}
          </div>
        </template>
        <template #warnings="{ row }">{{ row.employee.warnings ? row.employee.warnings.length : '-' }}</template>
        <template #issued_equipment_items="{ row }">
          {{ row.employee.issued_equipment_items ? row.employee.issued_equipment_items.length : '-' }}
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <app-button @click.native="onShowRenewPopup(row)" size="small">Renew</app-button>
            <app-button @click.native="onShowTerminatePopup(row)" size="small" theme="error">Terminate</app-button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :click-handler="onClickPaginationContractExpire"
            v-if="pagination_contract_expire.pages > 1"
            v-model="pagination_contract_expire.page"
            :page-count="pagination_contract_expire.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records
          :all="pagination_contract_expire.count"
          :count="table_contract_expire.items.length"
          :page="pagination_contract_expire.page"
          :limit="5"
        />
      </app-cells>
      <template v-if="table_renewal.items.length">
        <app-cells>
          <h2 class="title">Waiting renewal</h2>
        </app-cells>
        <v-client-table
          :data="table_renewal.items"
          :columns="table_renewal.columns"
          :options="table_renewal.options"
          class="table-default"
          :class="{ 'table-default--loading': renewal_preloader }"
        >
          <template #h__employee><div class="table-default__left">Name</div></template>
          <template #h__department><div class="table-default__left">Department</div></template>
          <template #h__position><div class="table-default__left">Position</div></template>
          <template #h__contract_expire><div class="table-default__left">Contract expire</div></template>
          <template #h__employment_status><div class="table-default__left">Status</div></template>
          <template #employee="{ row }">
            <div class="table-default__left table-default__left--photo">
              <div class="table-default__photo">
                <img :src="getUserAvatar(row.employee.crop.crop_url, row.employee.gender?.code || null)" alt="Avatar">
              </div>
              <router-link :to="{ name: 'employees-contracts-salaries', params: { id: row.employee.id } }" class="table-default__link-border">
                {{ row.employee.full_name }}
              </router-link>
            </div>
          </template>
          <template #department="{ row }"><div class="table-default__left">{{ row.employee.department.name }}</div></template>
          <template #position="{ row }"><div class="table-default__left">{{ row.employee.position.name }}</div></template>
          <template #contract_expire="{ row }">
            <div class="table-default__left">{{ dateFormat(row.to_date) }}</div>
          </template>
          <template #period="{ row }">{{ row.renewal_months ? `${row.renewal_months} month` : '-' }}</template>
          <template #basic="{ row }">{{ row.renewal_basic ? `${row.renewal_basic.toLocaleString()} TSh` : '-' }}</template>
          <template #allowance="{ row }">{{ row.renewal_allowance ? `${row.renewal_allowance.toLocaleString()} TSh` : '-' }}</template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :click-handler="onClickPaginationRenewal"
              v-if="pagination_renewal.pages > 1"
              v-model="pagination_renewal.page"
              :page-count="pagination_renewal.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records
            :all="pagination_renewal.count"
            :count="table_renewal.items.length"
            :page="pagination_renewal.page"
            :limit="5"
          />
        </app-cells>
      </template>
      <template v-if="table_termination.items.length">
        <app-cells>
          <h2 class="title">Waiting termination</h2>
        </app-cells>
        <v-client-table
          :data="table_termination.items"
          :columns="table_termination.columns"
          :options="table_termination.options"
          class="table-default"
          :class="{ 'table-default--loading': termination_preloader }"
        >
          <template #h__employee><div class="table-default__left">Name</div></template>
          <template #h__department><div class="table-default__left">Department</div></template>
          <template #h__position><div class="table-default__left">Position</div></template>
          <template #h__contract_expire><div class="table-default__left">Contract expire</div></template>
          <template #h__employment_status><div class="table-default__left">Status</div></template>
          <template #employee="{ row }">
            <div class="table-default__left table-default__left--photo">
              <div class="table-default__photo">
                <img :src="getUserAvatar(row.employee.crop.crop_url, row.employee.gender?.code || null)" alt="Avatar">
              </div>
              <router-link :to="{ name: 'employees-contracts-salaries', params: { id: row.employee.id } }" class="table-default__link-border">
                {{ row.employee.full_name }}
              </router-link>
            </div>
          </template>
          <template #department="{ row }"><div class="table-default__left">{{ row.employee.department.name }}</div></template>
          <template #position="{ row }"><div class="table-default__left">{{ row.employee.position.name }}</div></template>
          <template #contract_expire="{ row }">
            <div class="table-default__left">{{ dateFormat(row.to_date) }}</div>
          </template>
          <template #employment_status="{ row }">
            <div class="table-default__left">
              <template v-if="row.employment_status">
                <div v-if="row.to_date && row.employment_status.code === 'working'">
                  {{ row.employment_status.name }} {{ row.employment_type ? `(${row.employment_type.name})` : '' }} {{ row.employment_status.code === 'working' ? row.employee.seniority : '' }}
                </div>
                <div v-if="row.to_date && row.employment_status.code !== 'working'">
                  {{ row.employment_status.name }} {{ row.employment_type ? `(${row.employment_type.name})` : '' }}
                </div>
                <div v-if="!row.to_date">
                  {{ row.employment_status.name }} {{ row.employment_type ? `(${row.employment_type.name})` : '' }} {{ row.employment_status.code === 'working' ? row.employee.seniority : '' }}
                </div>
              </template>
              <template v-else>-</template>
            </div>
          </template>
          <template #warnings="{ row }">{{ row.employee.warnings ? row.employee.warnings.length : '-' }}</template>
          <template #issued_equipment_items="{ row }">
            {{ row.employee.issued_equipment_items ? row.employee.issued_equipment_items.length : '-' }}
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :click-handler="onClickPaginationTermination"
              v-if="pagination_termination.pages > 1"
              v-model="pagination_termination.page"
              :page-count="pagination_termination.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records
            :all="pagination_termination.count"
            :count="table_termination.items.length"
            :page="pagination_termination.page"
            :limit="5"
          />
        </app-cells>
      </template>
    </template>
    <modal
      name="renew-popup"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('renew-popup')" class="popup__close" type="button"></button>
        <div class="popup__title">Renew contract instructions</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckRenewForm">
            <app-form-group label="New contract period (in month)" label-for="renewal_months" required>
              <app-input
                v-model.number="form_renew.renewal_months"
                @change.native="$v.form_renew.renewal_months.$touch()"
                :error="$v.form_renew.renewal_months.$error"
                id="renewal_months"
                placeholder="0"
                name="renewal_months"
                type="number"
              />
              <template #error>
                <div v-if="$v.form_renew.renewal_months.$dirty && !$v.form_renew.renewal_months.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Basic salary" label-for="renewal_basic" required>
              <app-fin-number
                v-model="form_renew.renewal_basic"
                @change.native="$v.form_renew.renewal_basic.$touch()"
                :error="$v.form_renew.renewal_basic.$error"
                id="renewal_basic"
                placeholder="0"
                name="renewal_basic"
              />
              <template #error>
                <div v-if="$v.form_renew.renewal_basic.$dirty && !$v.form_renew.renewal_basic.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Allowance" label-for="renewal_allowance" required>
              <app-fin-number
                v-model="form_renew.renewal_allowance"
                @change.native="$v.form_renew.renewal_allowance.$touch()"
                :error="$v.form_renew.renewal_allowance.$error"
                id="renewal_allowance"
                placeholder="0"
                name="renewal_allowance"
              />
              <template #error>
                <div v-if="$v.form_renew.renewal_allowance.$dirty && !$v.form_renew.renewal_allowance.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Additional notes" label-for="renewal_note">
              <app-textarea
                v-model="form_renew.renewal_note"
                rows="5"
                placeholder="Write a note"
                id="renewal_note"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form_renew.$error" type="submit" ref="submit_renew">Renew</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="terminate-popup"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('terminate-popup')" class="popup__close" type="button"></button>
        <div class="popup__title">Terminate employee instructions</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckTerminateForm">
            <app-form-group label="Additional notes" label-for="notes" required>
              <app-textarea
                v-model="form_terminate.renewal_note"
                rows="5"
                placeholder="Write a note"
                id="notes"
                :error="$v.form_terminate.renewal_note.$error"
              />
              <template #error>
                <div v-if="$v.form_terminate.renewal_note.$dirty && !$v.form_terminate.renewal_note.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form_terminate.$error" type="submit_terminate" ref="submit_terminate">Terminate</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>

import { getAllEmployeesContracts, patchAllEmployeesContractRenewal } from '@/http'
import { format, parse } from 'date-fns'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'EmployeesContractRenewals',
  data() {
    return {
      is_loaded: false,
      contract_expire_preloader: false,
      table_contract_expire: {
        items: [],
        columns: [
          'employee',
          'department',
          'position',
          'contract_expire',
          'warnings',
          'issued_equipment_items',
          'seniority',
          'controls'
        ],
        options: {
          headings: {
            'employee': 'Name',
            'department': 'Department',
            'position': 'Position',
            'contract_expire': 'Contract expire',
            'warnings': 'Warnings',
            'issued_equipment_items': 'Items',
            'seniority': 'Seniority',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 5,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        }
      },
      pagination_contract_expire: {
        pages: 1,
        page: 1,
        count: 1,
      },
      renewal_preloader: false,
      table_renewal: {
        items: [],
        columns: [
          'employee',
          'department',
          'position',
          'contract_expire',
          'period',
          'basic',
          'allowance',
        ],
        options: {
          headings: {
            employee: 'Name',
            department: 'Department',
            position: 'Position',
            contract_expire: 'Contract expire',
            period: 'Period',
            basic: 'Basic',
            allowance: 'Allowance',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 5,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        }
      },
      pagination_renewal: {
        pages: 1,
        page: 1,
        count: 1,
      },
      termination_preloader: false,
      table_termination: {
        items: [],
        columns: [
          'employee',
          'department',
          'position',
          'contract_expire',
          'warnings',
          'issued_equipment_items',
          'employment_status',
        ],
        options: {
          headings: {
            'employee': 'Name',
            'department': 'Department',
            'position': 'Position',
            'contract_expire': 'Contract expire',
            'warnings': 'Warnings',
            'issued_equipment_items': 'Items',
            'employment_status': 'Status',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 5,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        }
      },
      pagination_termination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      form_renew: {},
      form_terminate: {},
    }
  },
  created() {
    if (!this.is_general_manager) {
      this.table_contract_expire.columns.pop()
      delete this.table_contract_expire.options.headings.controls
    }
    Promise.all([
      getAllEmployeesContracts({
        page: this.pagination_contract_expire.page,
        page_size: 5,
        contract_expire: true,
        renewal_status: 'wait',
        employment_status: 'working',
      }),
      getAllEmployeesContracts({
        page: this.pagination_renewal.page,
        page_size: 5,
        renewal_status: 'renew',
        contract_renewed: false,
      }),
      getAllEmployeesContracts({
        page: this.pagination_termination.page,
        page_size: 5,
        renewal_status: 'not_renew',
        employment_status: 'working',
      })
    ])
      .then(res => {
        this.table_contract_expire.items = res[0].data.results
        this.pagination_contract_expire.pages = res[0].data.pages
        this.pagination_contract_expire.count = res[0].data.count
        this.contract_expire_preloader = false

        this.table_renewal.items = res[1].data.results
        this.pagination_renewal.pages = res[1].data.pages
        this.pagination_renewal.count = res[1].data.count
        this.renewal_preloader = false

        this.table_termination.items = res[2].data.results
        this.pagination_termination.pages = res[2].data.pages
        this.pagination_termination.count = res[2].data.count
        this.termination_preloader = false

        this.is_loaded = true
      })
  },
  validations: {
    form_renew: {
      renewal_months: { required },
      renewal_basic: { required },
      renewal_allowance: { required },
    },
    form_terminate: {
      renewal_note: { required },
    },
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  methods: {
    fetchContractExpireItems() {
      this.contract_expire_preloader = true
      getAllEmployeesContracts({
        page: this.pagination_contract_expire.page,
        page_size: 5,
        contract_expire: true,
        renewal_status: 'wait',
        employment_status: 'working',
      })
        .then(res => {
          this.table_contract_expire.items = res.data.results
          this.pagination_contract_expire.pages = res.data.pages
          this.pagination_contract_expire.count = res.data.count
          this.contract_expire_preloader = false
          this.is_loaded = true
        })
    },
    fetchRenewalItems() {
      this.renewal_preloader = true
      getAllEmployeesContracts({
        page: this.pagination_renewal.page,
        page_size: 5,
        renewal_status: 'renew',
        contract_renewed: false,
      })
        .then(res => {
          this.table_renewal.items = res.data.results
          this.pagination_renewal.pages = res.data.pages
          this.pagination_renewal.count = res.data.count
          this.renewal_preloader = false

          this.is_loaded = true
        })
    },
    fetchTerminationItems() {
      this.termination_preloader = true
      getAllEmployeesContracts({
        page: this.pagination_termination.page,
        page_size: 5,
        renewal_status: 'not_renew',
        employment_status: 'working',
      })
        .then(res => {
          this.table_termination.items = res.data.results
          this.pagination_termination.pages = res.data.pages
          this.pagination_termination.count = res.data.count
          this.termination_preloader = false

          this.is_loaded = true
        })
    },
    onClickPaginationContractExpire(page) {
      this.pagination_contract_expire.page = page
      this.fetchContractExpireItems()
    },
    onClickPaginationRenewal(page) {
      this.pagination_renewal.page = page
      this.fetchRenewalItems()
    },
    onClickPaginationTermination(page) {
      this.pagination_termination.page = page
      this.fetchTerminationItems()
    },
    onCheckRenewForm() {
      this.$v.form_renew.$touch()
      if (this.$v.form_renew.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendRenewForm()
      }
    },
    sendRenewForm() {
      this.$refs.submit_renew.preload = true
      patchAllEmployeesContractRenewal(this.form_renew.id, this.normalizeRenewForm())
        .then(() => {
          this.$modal.hide('renew-popup')
          this.$notify({
            type: 'success',
            title: 'Contract saved',
          })
          this.fetchContractExpireItems()
        })
        .catch(() => { this.$refs.submit_renew.preload = false })
    },
    normalizeRenewForm() {
      const normalizedForm = { ...this.form_renew }

      normalizedForm.renewal_status = 'renew'

      return normalizedForm
    },
    onCheckTerminateForm() {
      this.$v.form_terminate.$touch()
      if (this.$v.form_terminate.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendTerminateForm()
      }
    },
    sendTerminateForm() {
      this.$refs.submit_terminate.preload = true
      patchAllEmployeesContractRenewal(this.form_terminate.id, { ...this.form_terminate, renewal_status: 'not_renew' })
        .then(() => {
          this.$modal.hide('terminate-popup')
          this.$notify({
            type: 'success',
            title: 'Contract saved',
          })
          this.fetchContractExpireItems()
        })
        .catch(() => { this.$refs.submit_terminate.preload = false })
    },
    onShowRenewPopup(row) {
      this.form_renew = { id: row.id, renewal_months: 12 }
      if (row.last_salary) {
        this.form_renew.renewal_basic = row.last_salary.basic
        this.form_renew.renewal_allowance = row.last_salary.allowance
      }
      this.$v.form_renew.$reset()
      this.$modal.show('renew-popup')
    },
    onShowTerminatePopup(row) {
      this.form_terminate = { id: row.id }
      this.$v.form_terminate.$reset()
      this.$modal.show('terminate-popup')
    },
    getUserAvatar(crop_url, gender) {
      if (crop_url) return crop_url
      else {
        if (!gender) {
          return require('@/assets/img/temp/avatar-slug.jpg')
        } else if (gender === 'male') {
          return require('@/assets/img/temp/avatar-slug-male.jpg')
        } else if (gender === 'female') {
          return require('@/assets/img/temp/avatar-slug-female.jpg')
        }
      }
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
  }
}
</script>
